import { getCarouselImages } from 'apis/dashboardApis'
import { Loader } from 'components/Loader'
import { saveAs } from 'file-saver'
import { useFetch } from 'hooks/useFetch'
import { Button, Carousel, OverlayTrigger } from 'react-bootstrap'
import { LazyLoadImage } from 'react-lazy-load-image-component'
import { useGlobalStore } from 'store/useGlobalStore'
import { AdvertType, CarouselImage } from '../../types/baseTypes'
import { PopoverContainer, StyledHomeSlideShow, StyledPromotion, StyledSearch, StyledVideo } from './SlideShow.styled'
import ReactPlayer from 'react-player/lazy'
import { useEffect, useRef, useState } from 'react'

export const SlideShow = () => {
  const { loading, data } = useFetch(() => getCarouselImages())
  const [assetFolder] = useGlobalStore((state) => [state.userClaims.CurrentBuyerTypeAssetFolder])
  const [interval, setInterval] = useState(3000)
  const [activeIndex, setActiveIndex] = useState(0)
  const playerRefs = useRef<(ReactPlayer | null)[]>([])
  const [carouselItems, setCarouselItems] = useState<CarouselImage[]>([])

  const clickHandler = (e: React.MouseEvent<HTMLDivElement, MouseEvent>, item: CarouselImage) => {
    e.preventDefault()
    if (item.AdvertType === AdvertType.PictureOnly || item.AdvertType === AdvertType.MediaOnly) return
    if (item.DocumentURL) saveAs(item.DocumentURL, item.DocumentName)
  }

  const handleDuration = (duration: number, eventIndex: number) => {
    if (eventIndex === activeIndex) {
      setInterval(duration * 1000)
    }
    setCarouselItems((prev) =>
      prev.map((item, index) => {
        if (index === eventIndex) {
          return { ...item, Duration: duration }
        }
        return item
      })
    )
  }

  const handleSlide = (eventKey: number) => {
    setActiveIndex(eventKey)
    if (carouselItems[eventKey]?.IsVideo) {
      if (playerRefs.current[eventKey]) {
        playerRefs.current[eventKey]?.seekTo(0)
      }
      setInterval(carouselItems[eventKey].Duration * 1000)
    } else {
      setInterval(3000)
    }
  }

  const handleVideoEnded = (index: number) => {
    // Exit fullscreen if it's active
    if (document.fullscreenElement) {
      document.exitFullscreen().catch((err) => {
        console.error('Error exiting fullscreen:', err)
      })
    }

    // Wait a small delay to ensure fullscreen is exited before changing slides
    setTimeout(() => {
      if (index < carouselItems.length - 1) handleSlide(index + 1)
      else handleSlide(0)
    }, 100)
  }

  useEffect(() => {
    if (data) {
      setCarouselItems(data)
    }
  }, [data])

  return (
    <StyledHomeSlideShow assetFolder={assetFolder}>
      <Carousel
        interval={interval}
        fade={true}
        controls={false}
        indicators={carouselItems?.length > 1}
        activeIndex={activeIndex}
        onSelect={handleSlide}
      >
        {!loading ? (
          carouselItems?.map((item, index) => (
            <Carousel.Item key={index}>
              {item?.IsVideo ? (
                <StyledVideo>
                  <ReactPlayer
                    ref={(el) => (playerRefs.current[index] = el)}
                    url={item.ImageURL}
                    controls={true}
                    muted={true}
                    width="100%"
                    height="100%"
                    playing={index === activeIndex}
                    onDuration={(duration) => handleDuration(duration, index)}
                    onEnded={() => handleVideoEnded(index)}
                    onContextMenu={(e) => e.preventDefault()}
                    config={{
                      file: {
                        attributes: {
                          controlsList: 'nodownload noplaybackrate',
                          disablePictureInPicture: true
                        }
                      }
                    }}
                  />
                </StyledVideo>
              ) : (
                <LazyLoadImage
                  // eslint-disable-next-line jsx-a11y/alt-text
                  placeholder={<img className="d-block w-100" />}
                  onClick={(e) => clickHandler(e, item)}
                  className={`d-block w-100 h-100 ${
                    item.AdvertType !== AdvertType.PictureOnly &&
                    item.AdvertType !== AdvertType.MediaOnly &&
                    'clickable'
                  }`}
                  src={item.ImageURL}
                />
              )}
              <Carousel.Caption
                style={
                  item.AdvertType !== AdvertType.PictureOnly && item.AdvertType !== AdvertType.MediaOnly
                    ? { marginTop: '-3rem', paddingBottom: '0' }
                    : null
                }
              >
                {item.AdvertType !== AdvertType.PictureOnly && item.AdvertType !== AdvertType.MediaOnly && (
                  <StyledPromotion style={{ paddingTop: '-1rem' }}>
                    <OverlayTrigger
                      trigger={['hover', 'click']}
                      placement="bottom"
                      overlay={<PopoverContainer>{item.Description}</PopoverContainer>}
                    >
                      <span>{item.Description}</span>
                    </OverlayTrigger>
                    <StyledSearch onClick={(e) => clickHandler(e, item)}>
                      <Button>LEARN MORE</Button>
                    </StyledSearch>
                  </StyledPromotion>
                )}
              </Carousel.Caption>
            </Carousel.Item>
          ))
        ) : (
          <Carousel.Item>
            <img className="d-block w-100" alt={''} />
            <Loader overlay />
          </Carousel.Item>
        )}
      </Carousel>
    </StyledHomeSlideShow>
  )
}
